import type { ComponentConfig } from "@puckeditor/core";
import { ContentBlock, type ContentBlockProps } from "./content-block";
import { ContentBlockFields } from "./content-block.fields";
import { ImagePicker } from "../editor/ImagePicker";

export const ContentBlockConfig: ComponentConfig<ContentBlockProps> = {
  label: "Content Block",
  fields: {
    heading: {
      ...ContentBlockFields.heading,
      contentEditable: true,
    },
    content: {
      ...ContentBlockFields.content,
      contentEditable: true,
    },
    imageUrl: {
      ...ContentBlockFields.imageUrl,
      render: ({
        value,
        onChange,
      }: {
        value: string | undefined;
        onChange: (val: string | undefined) => void;
      }) => <ImagePicker value={value || ""} onChange={onChange} label="Image" />,
    },
    imageAlt: ContentBlockFields.imageAlt,
    layout: ContentBlockFields.layout,
    imageStyle: ContentBlockFields.imageStyle,
    backgroundColor: ContentBlockFields.backgroundColor,
    headingSize: ContentBlockFields.headingSize,
  },
  /**
   * Nothing reads the image in the text-only layout, and with no image set the three
   * image layouts render the same thing as each other.
   */
  resolveFields: (data, { fields }) => {
    const props = (data.props || {}) as { layout?: string; imageUrl?: string };
    const visible = { ...(fields as Record<string, unknown>) };
    const hasImage = Boolean(props.imageUrl?.trim());

    if (props.layout === "no-image" || props.layout === "statement" || !hasImage) {
      delete visible.imageAlt;
      delete visible.imageStyle;
    }

    if (props.layout === "no-image" || props.layout === "statement") {
      delete visible.imageUrl;
    }

    // The statement heading is the display element; its size is the layout.
    if (props.layout === "statement") {
      delete visible.headingSize;
    }

    return visible as typeof fields;
  },
  defaultProps: {
    heading: "Your Heading Here",
    content:
      "Add your content here. You can include multiple paragraphs by separating them with double line breaks.\n\nThis is a second paragraph to demonstrate the formatting.",
    layout: "image-left",
    imageStyle: "shadow",
    backgroundColor: "white",
    headingSize: "large",
    imageAlt: "",
  },
  // Spread rather than list props: an enumerated signature silently drops any
  // field added later (imageStyle did exactly that, so the image always kept
  // the "shadow" default).
  render: ({ id, puck, editMode, ...props }) => {
    return <ContentBlock id={id} {...props} />;
  },
};
